import React from 'react';
import { linkifyText } from '@/utils/linkify';
import type { AgentApiProvidedJobPostsContent } from '@/types/serializers';

export interface PdfData {
  title: string;
  companyName: string;
  companyInfo: {
    industry: string;
    address: string;
    url: string;
    establishedDate: string;
    listedPhase: string;
    presidentName: string;
    numberOfEmployees: string;
    description: string;
  };
  jobContent: Array<AgentApiProvidedJobPostsContent>;
}

export const ProvidedJobPostPrintView: React.FC<{ data: PdfData }> = ({ data }) => {
  return (
    <div
      className="w-[890px] bg-white px-[55px] pt-[55px] pb-[79px] text-body-sm text-main"
      style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
    >
      {/* タイトル */}
      <h1 className="mb-md text-center text-heading-lg font-bold">{data.title}</h1>

      {/* 企業情報セクション */}
      <div>
        {/* セクションヘッダー */}
        <div
          className="mb-0 border border-solid border-border bg-[#F6F6F6] px-md"
          data-pdf-row="true"
        >
          <h2 className="m-0 flex h-[53px] items-center text-heading-sm font-medium">企業情報</h2>
        </div>

        {/* 企業名 | 業種 */}
        <div className="flex w-full" data-pdf-row="true">
          <div className="flex min-h-[37px] w-[179px] items-center border border-t-0 border-solid border-border bg-[#F6F6F6] px-md font-medium">
            企業名
          </div>
          <div className="flex min-h-[37px] flex-1 items-center border border-t-0 border-l-0 border-solid border-border bg-white px-md">
            {data.companyName}
          </div>
          <div className="flex min-h-[37px] w-[179px] items-center border border-t-0 border-l-0 border-solid border-border bg-[#F6F6F6] px-md font-medium">
            業種
          </div>
          <div className="flex min-h-[37px] flex-1 items-center border border-t-0 border-l-0 border-solid border-border bg-white px-md">
            {data.companyInfo.industry}
          </div>
        </div>

        {/* 住所 */}
        <div className="flex w-full" data-pdf-row="true">
          <div className="flex min-h-[37px] w-[179px] items-center border border-t-0 border-solid border-border bg-[#F6F6F6] px-md font-medium">
            住所
          </div>
          <div className="flex min-h-[37px] flex-1 items-center border border-t-0 border-l-0 border-solid border-border bg-white px-md">
            {data.companyInfo.address}
          </div>
        </div>

        {/* URL */}
        <div className="flex w-full" data-pdf-row="true">
          <div className="flex min-h-[37px] w-[179px] items-center border border-t-0 border-solid border-border bg-[#F6F6F6] px-md font-medium">
            URL
          </div>
          <div className="flex min-h-[37px] flex-1 items-center border border-t-0 border-l-0 border-solid border-border bg-white px-md break-all">
            {data.companyInfo.url}
          </div>
        </div>

        {/* 設立年 | 上場情報 */}
        <div className="flex w-full" data-pdf-row="true">
          <div className="flex min-h-[37px] w-[179px] items-center border border-t-0 border-solid border-border bg-[#F6F6F6] px-md font-medium">
            設立年
          </div>
          <div className="flex min-h-[37px] flex-1 items-center border border-t-0 border-l-0 border-solid border-border bg-white px-md">
            {data.companyInfo.establishedDate}
          </div>
          <div className="flex min-h-[37px] w-[179px] items-center border border-t-0 border-l-0 border-solid border-border bg-[#F6F6F6] px-md font-medium">
            上場情報
          </div>
          <div className="flex min-h-[37px] flex-1 items-center border border-t-0 border-l-0 border-solid border-border bg-white px-md">
            {data.companyInfo.listedPhase}
          </div>
        </div>

        {/* 代表者名 | 従業員数 */}
        <div className="flex w-full" data-pdf-row="true">
          <div className="flex min-h-[37px] w-[179px] items-center border border-t-0 border-solid border-border bg-[#F6F6F6] px-md font-medium">
            代表者名
          </div>
          <div
            className={`flex min-h-[37px] flex-1 items-center border border-t-0 border-l-0 border-solid border-border bg-white px-md ${
              data.companyInfo.presidentName === '----' ? 'text-sub' : ''
            }`}
          >
            {data.companyInfo.presidentName}
          </div>
          <div className="flex min-h-[37px] w-[179px] items-center border border-t-0 border-l-0 border-solid border-border bg-[#F6F6F6] px-md font-medium">
            従業員数
          </div>
          <div className="flex min-h-[37px] flex-1 items-center border border-t-0 border-l-0 border-solid border-border bg-white px-md">
            {data.companyInfo.numberOfEmployees}
          </div>
        </div>

        {/* 事業内容 */}
        <div className="flex w-full" data-pdf-row="true">
          <div className="flex min-h-[37px] w-[179px] items-start border border-t-0 border-solid border-border bg-[#F6F6F6] px-md pt-xs font-medium">
            事業内容
          </div>
          <div className="min-h-[37px] flex-1 border border-t-0 border-l-0 border-solid border-border bg-white px-md pt-xs whitespace-pre-wrap">
            {data.companyInfo.description}
          </div>
        </div>
      </div>

      {/* 求人内容セクション */}
      <div>
        {/* セクションヘッダー */}
        <div
          className="mb-0 border-x border-b border-solid border-border bg-[#F6F6F6] px-md"
          data-pdf-row="true"
        >
          <h2 className="m-0 flex h-[53px] items-center text-heading-sm font-medium">求人内容</h2>
        </div>

        {/* 求人内容項目 */}
        {data.jobContent.map((item, index) => (
          <div key={index} className="flex w-full" data-pdf-row="true">
            <div className="flex w-[179px] items-start border border-t-0 border-solid border-border bg-[#F6F6F6] px-md py-xs text-sm font-medium">
              {item.key}
            </div>
            <div className="flex-1 border border-t-0 border-l-0 border-solid border-border bg-white px-md py-xs text-sm whitespace-pre-wrap">
              {linkifyText(item.content, 'text-link underline')}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
